import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Typography } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import CanvasLineLayer, { CanvasLine } from './CanvasLineLayer';
import CanvasStopLayer, { CanvasStop } from './CanvasStopLayer';
import {
  getNetworkRoutes,
  getRouteShape,
  type Bbox,
  type NetworkStop,
} from '../../../services/network/networkApi';
import { decodePolyline } from '../../../utils/map/simplifyPath';
import { generateRouteColor } from '../../../utils/fixedRoutes/routeToLineConverter';
import type { NetworkDisplayStatus } from '../../../context/NetworkBrowseContext';

export type { NetworkDisplayStatus };

/**
 * In-map hosted-network display (PRD-04 FR7/FR8). Backend-heavy — the browser only
 * holds the current viewport + selection.
 *
 * - **Routes** are always shown while the feed is toggled on (no zoom gate); the
 *   server caps + simplifies per zoom and flags `truncated`.
 * - **Stops** are shown only for a selected route (at any zoom), plus a searched/
 *   focused stop. The whole network's stops are never rendered — too costly to fetch
 *   + draw on every pan/zoom when nothing is selected.
 *
 * Data is fetched for a **padded** bbox and cached, so panning / small zooms reuse it
 * instead of refetching; the canvas layers render it with `viewportCull={false}` (the
 * server already scoped it), which eliminates the zoom appear/disappear flicker.
 */

const DEBOUNCE_MS = 300;
const PAD = 0.5; // fetch this fraction beyond the viewport so pans reuse the data
const ROUTE_CAP = 2000;
const STOP_CAP = 4000;

interface Props {
  feedId: string;
  selectedRouteId: string | null;
  onSelectRoute: (id: string | null) => void;
  selectedStopId: string | null;
  onSelectStop: (id: string | null) => void;
  onStatus?: (status: NetworkDisplayStatus) => void;
  /** "Zoom to route" signal from the drawer; nonce changes => fit the map to that route. */
  zoomRequest?: { routeId: string; nonce: number } | null;
  /** A searched/picked stop to fly to + pin; nonce changes => re-fly. */
  focusedStop?: { stopId: string; lat: number; lng: number; name?: string | null; nonce: number } | null;
  /** Chosen direction/shape variant for the selected route (PRD-04 FR6a); routeId-scoped. */
  selectedVariant?: { routeId: string; direction: number | null; shapeId: string | null } | null;
  /** "Edit" from a route's popup — forks the live route into the Fixed Routes drawer (FR15/FR16). */
  onEditRoute?: (routeId: string) => void;
}

function stopToCanvas(s: NetworkStop): CanvasStop {
  return { stopId: s.stopId, lat: s.stopLat, lng: s.stopLon, name: s.stopName ?? undefined, important: true };
}

function padBbox([w, s, e, n]: Bbox, f: number): Bbox {
  const dx = (e - w) * f;
  const dy = (n - s) * f;
  return [w - dx, s - dy, e + dx, n + dy];
}

function bboxContains(outer: Bbox, inner: Bbox): boolean {
  return outer[0] <= inner[0] && outer[1] <= inner[1] && outer[2] >= inner[2] && outer[3] >= inner[3];
}

interface RouteMeta {
  shortName?: string | null;
  longName?: string | null;
  type?: number | null;
}

// GTFS route_type → i18n key (networkRoutes.mode.*).
const MODE_KEY: Record<number, string> = {
  0: 'tram',
  1: 'metro',
  2: 'rail',
  3: 'bus',
  4: 'ferry',
  5: 'cableTram',
  6: 'aerial',
  7: 'funicular',
  11: 'trolleybus',
  12: 'monorail',
};

/** Click-popup for a network route line (PRD-04) — basic info from the lightweight overview,
 * plus an "Edit a copy" action that forks the live route into the Fixed Routes drawer. */
const RouteLinePopup: React.FC<{ meta?: RouteMeta; routeId: string; onEdit?: () => void }> = ({
  meta,
  routeId,
  onEdit,
}) => {
  const { t } = useTranslation(['mapCore']);
  const modeKey = meta?.type != null ? MODE_KEY[meta.type] : undefined;
  return (
    <Box sx={{ minWidth: 150, maxWidth: 240 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {meta?.shortName || routeId}
      </Typography>
      {meta?.longName && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {meta.longName}
        </Typography>
      )}
      <Chip size="small" label={t(`networkRoutes.mode.${modeKey ?? 'other'}`)} sx={{ mt: 0.75, height: 20 }} />
      {onEdit && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditNoteIcon />}
          onClick={onEdit}
          sx={{ mt: 1, display: 'flex', textTransform: 'none' }}
        >
          {t('networkRoutes.editCopy')}
        </Button>
      )}
    </Box>
  );
};

const HostedNetworkDisplay: React.FC<Props> = ({
  feedId,
  selectedRouteId,
  onSelectRoute,
  selectedStopId,
  onSelectStop,
  onStatus,
  zoomRequest,
  focusedStop,
  selectedVariant,
  onEditRoute,
}) => {
  const map = useMap();
  // One canvas renderer shared by every vector layer below. Leaflet 1.x delivers a DOM click
  // only to the topmost canvas pane, so giving each layer its own `L.canvas()` lets the
  // selected-route overlay / stop panes (added on selection) swallow clicks meant for the
  // route lines beneath — making other routes unclickable until the feed is toggled. One
  // renderer => one canvas => Leaflet hit-tests all lines + stops together.
  const sharedRenderer = useRef(L.canvas({ padding: 0.5 }));
  const [overviewLines, setOverviewLines] = useState<CanvasLine[]>([]);
  const [routeStops, setRouteStops] = useState<CanvasStop[]>([]);
  const [selectedShapeLine, setSelectedShapeLine] = useState<CanvasLine | null>(null);
  const [routeMetaById, setRouteMetaById] = useState<Record<string, RouteMeta>>({});

  const onStatusRef = useRef(onStatus);
  useEffect(() => {
    onStatusRef.current = onStatus;
  }, [onStatus]);

  // The edit handler's identity is unstable (its source `openRightDrawer` isn't memoized),
  // so keep it in a ref — otherwise `renderRoutePopup` (and the whole overview `elements`
  // memo downstream) would rebuild on every parent render. `canEdit` gates the button.
  const onEditRouteRef = useRef(onEditRoute);
  useEffect(() => {
    onEditRouteRef.current = onEditRoute;
  }, [onEditRoute]);
  const canEdit = !!onEditRoute;

  // Viewport-driven route fetch with a padded-bbox cache (reuse on pan / small zoom) +
  // abort. The cache resets when the feed changes. Stops are NOT fetched here — only a
  // selected route's stops are shown (see the selection effect below).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let routesCtl: AbortController | null = null;
    let routesCache: { zoom: number; bbox: Bbox } | null = null;

    const run = () => {
      const zoom = map.getZoom();
      const b = map.getBounds();
      const vbbox: Bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];

      // Routes — always visible while toggled on.
      const zoomBucket = Math.round(zoom);
      if (!(routesCache && routesCache.zoom === zoomBucket && bboxContains(routesCache.bbox, vbbox))) {
        const padded = padBbox(vbbox, PAD);
        routesCache = { zoom: zoomBucket, bbox: padded };
        routesCtl?.abort();
        routesCtl = new AbortController();
        getNetworkRoutes(feedId, padded, zoom, { signal: routesCtl.signal })
          .then((res) => {
            setOverviewLines(
              res.routes.map((r) => ({
                id: r.routeId,
                // Distinct per-route colour — feeds commonly brand every route (esp. buses)
                // with one route_color, which makes a whole network unreadable.
                coordinates: decodePolyline(r.polyline),
                color: generateRouteColor(r.routeId),
                weight: 3.5, // thicker than the 2px default so lines are easy to see
              })),
            );
            const meta: Record<string, RouteMeta> = {};
            res.routes.forEach((r) => {
              meta[r.routeId] = { shortName: r.routeShortName, longName: r.routeLongName, type: r.routeType };
            });
            setRouteMetaById(meta);
            onStatusRef.current?.({ total: res.total, shown: res.routes.length, truncated: res.truncated });
          })
          .catch(() => undefined);
      }
    };

    const debounced = () => {
      clearTimeout(timer);
      timer = setTimeout(run, DEBOUNCE_MS);
    };

    run();
    map.on('moveend', debounced);
    map.on('zoomend', debounced);
    return () => {
      clearTimeout(timer);
      routesCtl?.abort();
      map.off('moveend', debounced);
      map.off('zoomend', debounced);
    };
  }, [map, feedId]);

  // Selected route -> its stops + full-shape overlay (the chosen variant if any). Shown at
  // any zoom; the overlay is the exact alignment (vs the simplified overview line). FR6a/5.2.
  useEffect(() => {
    if (!selectedRouteId) {
      setRouteStops([]);
      setSelectedShapeLine(null);
      return undefined;
    }
    const variant = selectedVariant && selectedVariant.routeId === selectedRouteId ? selectedVariant : null;
    const ctl = new AbortController();
    getRouteShape(feedId, selectedRouteId, {
      direction: variant?.direction ?? undefined,
      shapeId: variant?.shapeId ?? undefined,
      signal: ctl.signal,
    })
      .then((shapes) => {
        const shape = shapes[0];
        setRouteStops((shape?.stops ?? []).map(stopToCanvas));
        setSelectedShapeLine(
          shape && shape.coordinates?.length
            ? { id: shape.routeId, coordinates: shape.coordinates, color: generateRouteColor(shape.routeId) }
            : null,
        );
      })
      .catch(() => undefined);
    return () => ctl.abort();
  }, [feedId, selectedRouteId, selectedVariant]);

  // "Zoom to route" — fetch the route's geometry and fit the map to it (FR14). Keyed on
  // the request nonce so clicking the same route again re-fits.
  useEffect(() => {
    if (!zoomRequest?.routeId) return undefined;
    const ctl = new AbortController();
    getRouteShape(feedId, zoomRequest.routeId, { signal: ctl.signal })
      .then((shapes) => {
        const shape = shapes[0];
        if (!shape) return;
        const pts: Array<[number, number]> = shape.coordinates?.length
          ? shape.coordinates
          : (shape.stops ?? []).map((s) => [s.stopLat, s.stopLon]);
        if (pts.length) {
          map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 16 });
        }
      })
      .catch(() => undefined);
    return () => ctl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, feedId, zoomRequest?.nonce]);

  // Focused (searched) stop — fly to it. Keyed on nonce so re-picking re-flies.
  useEffect(() => {
    if (!focusedStop) return;
    map.setView([focusedStop.lat, focusedStop.lng], Math.max(map.getZoom(), 16));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, focusedStop?.nonce]);

  const renderRoutePopup = useCallback(
    (id: string) => (
      <RouteLinePopup
        meta={routeMetaById[id]}
        routeId={id}
        onEdit={
          canEdit
            ? () => {
                onEditRouteRef.current?.(id);
                map.closePopup();
              }
            : undefined
        }
      />
    ),
    [routeMetaById, canEdit, map],
  );

  const highlighted = selectedRouteId ? new Set([selectedRouteId]) : undefined;
  // Stops shown = the selected route's stops (if any) + a searched/focused stop. Deduped by
  // stopId — the focused stop can also be one of the route's stops, and duplicates would
  // collide on the React key in CanvasStopLayer; the focused entry (set last) wins. The whole
  // network's stops are deliberately never rendered — only a selection brings stops onto the map.
  const stopById = new Map<string, CanvasStop>();
  for (const s of routeStops) stopById.set(s.stopId, s);
  if (focusedStop) {
    stopById.set(focusedStop.stopId, {
      stopId: focusedStop.stopId,
      lat: focusedStop.lat,
      lng: focusedStop.lng,
      name: focusedStop.name ?? undefined,
      important: true,
    });
  }
  const stops = Array.from(stopById.values());

  return (
    <>
      <CanvasLineLayer
        lines={overviewLines}
        highlightedIds={highlighted}
        cap={ROUTE_CAP}
        viewportCull={false}
        onLineClick={onSelectRoute}
        renderPopup={renderRoutePopup}
        renderer={sharedRenderer.current}
      />
      {selectedShapeLine && (
        <CanvasLineLayer
          lines={[selectedShapeLine]}
          highlightedIds={new Set([selectedShapeLine.id])}
          cap={1}
          viewportCull={false}
          renderer={sharedRenderer.current}
          // Exact-alignment overlay only — non-interactive so clicks on the selected route
          // fall through to its clickable overview line beneath (re-select / deselect still work).
          interactive={false}
        />
      )}
      <CanvasStopLayer
        stops={stops}
        selectedStopId={selectedStopId}
        cap={STOP_CAP}
        viewportCull={false}
        onStopClick={onSelectStop}
        renderer={sharedRenderer.current}
      />
    </>
  );
};

export default HostedNetworkDisplay;
